// Screen: "Community Play"
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { connectSocket, getSocket, createRoom, joinRoom, startGame, spectateRoom } from '../socketClient';
import { getActiveRooms } from '../apiClient';

const hardwoodFloorStyle = {
  background: `
    repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
    linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
  `,
  backgroundBlendMode: 'overlay, overlay, normal'
};

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export default function CommunityPlayPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const lobbyMsgsRef = React.useRef(null);

  const [view, setView] = useState('lobby'); // lobby | waiting | join
  const [gameMode, setGameMode] = useState('buck');
  const [playerCount, setPlayerCount] = useState(3);
  const [aiCount, setAiCount] = useState(0);
  const [partnerMode, setPartnerMode] = useState('fixed');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(null); // null | 'buck' | 'euchre'

  const [roomCode, setRoomCode] = useState('');
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [players, setPlayers] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [socketStatus, setSocketStatus] = useState('connecting');
  const [openRooms, setOpenRooms] = useState([]);
  const [lobbyMessages, setLobbyMessages] = useState([]);
  const [lobbyInput, setLobbyInput] = useState('');

  // Poll for rooms always
  useEffect(() => {
    const fetchRooms = () => getActiveRooms().then(data => setOpenRooms(data.rooms || []));
    fetchRooms();
    const interval = setInterval(fetchRooms, 5000);
    return () => clearInterval(interval);
  }, []);

  // Connect socket
  useEffect(() => {
    if (!token) return;
    const sock = connectSocket(token);
    if (sock?.connected) {
      setSocketStatus('connected');
    } else if (sock) {
      sock.on('connect', () => setSocketStatus('connected'));
      sock.on('disconnect', () => setSocketStatus('disconnected'));
      sock.on('connect_error', (err) => { console.error('Socket connect_error:', err?.message); setSocketStatus('error'); });
      sock.io.on('reconnect_failed', () => setSocketStatus('error'));
      sock.io.on('reconnect', () => setSocketStatus('connected'));
    }
  }, [token]);

  // Socket room events
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onPlayerJoined = (data) => {
      setPlayers(data.players);
      if (data.playerCount) setPlayerCount(data.playerCount);
      if (data.aiCount !== undefined) setAiCount(data.aiCount);
    };
    const onPlayerLeft = (data) => { setPlayers(data.players); };
    const onGameStarting = (data) => {
      const params = new URLSearchParams({
        community: data.roomCode, players: data.playerCount, seat: data.seatIndex,
        ...(data.isHost ? { host: 'true' } : {}),
        ...(data.partnerMode && data.partnerMode !== 'fixed' ? { partnerMode: data.partnerMode } : {}),
        ...(data.teams ? { teams: JSON.stringify(data.teams) } : {}),
        seated: JSON.stringify(data.players.map(p => ({ seatIndex: p.seatIndex, nickname: p.nickname, userId: p.userId })))
      });
      navigate(`${data.gameMode === 'euchre' ? '/euchre' : '/play'}?${params.toString()}`);
    };
    const onHostDisconnected = () => {
      setError('Host left the room.');
      setView('lobby');
      setPlayers([]);
      setRoomCode('');
    };
    const onChatMessage = (data) => {
      setLobbyMessages(prev => [...prev.slice(-99), data]);
    };

    socket.on('player-joined', onPlayerJoined);
    socket.on('player-left', onPlayerLeft);
    socket.on('game-starting', onGameStarting);
    socket.on('host-disconnected', onHostDisconnected);
    socket.on('chat-message', onChatMessage);

    return () => {
      socket.off('player-joined', onPlayerJoined);
      socket.off('player-left', onPlayerLeft);
      socket.off('game-starting', onGameStarting);
      socket.off('host-disconnected', onHostDisconnected);
      socket.off('chat-message', onChatMessage);
    };
  }, [navigate, socketStatus]);

  useEffect(() => {
    if (lobbyMsgsRef.current) lobbyMsgsRef.current.scrollTop = lobbyMsgsRef.current.scrollHeight;
  }, [lobbyMessages]);

  const handleCreateRoom = useCallback(async (mode, pc, ac, pm, priv) => {
    setError('');
    setShowCreateModal(null);
    try {
      const result = await createRoom(pc, ac, mode, pm, priv);
      if (result.success) {
        setRoomCode(result.roomCode);
        setPlayers(result.room.players);
        setPlayerCount(result.room.playerCount || pc);
        setAiCount(result.room.aiCount || 0);
        setGameMode(mode);
        setPartnerMode(pm);
        setIsPrivate(priv);
        setIsHost(true);
        setView('waiting');
      } else {
        setError(result.error || 'Failed to create room');
      }
    } catch (e) {
      setError('Connection failed. Make sure the server is running and try refreshing.');
    }
  }, []);

  const handleJoinRoom = useCallback(async (codeOverride) => {
    setError('');
    const code = (codeOverride || joinCodeInput).toUpperCase();
    if (code.length < 6) { setError('Enter a 6-character room code'); return; }
    const result = await joinRoom(code);
    if (result.success) {
      setRoomCode(code);
      setPlayers(result.room.players);
      setPlayerCount(result.room.playerCount || 4);
      setAiCount(result.room.aiCount || 0);
      setIsHost(false);
      setView('waiting');
    } else {
      setError(result.error || 'Failed to join room');
    }
  }, [joinCodeInput]);

  const handleStartGame = useCallback(async () => {
    const result = await startGame();
    if (!result.success) setError(result.error || 'Failed to start game');
  }, []);

  const handleLeave = useCallback(() => {
    getSocket()?.emit('leave-room');
    setView('lobby');
    setPlayers([]);
    setRoomCode('');
    setIsHost(false);
    setLobbyMessages([]);
  }, []);

  const sendLobbyMessage = () => {
    const text = lobbyInput.trim();
    if (!text) return;
    getSocket()?.emit('chat-message', { text });
    setLobbyInput('');
  };

  const humanSlots = playerCount - aiCount;
  const allSeated = players.length >= humanSlots;
  const waitingRooms = openRooms.filter(r => !r.gameStarted);
  const liveRooms = openRooms.filter(r => r.gameStarted);

  // ── WAITING ROOM VIEW ─────────────────────────────────────────────────────
  if (view === 'waiting') {
    return (
      <div style={{ ...hardwoodFloorStyle, minHeight: '100vh', color: '#e2e8f0', fontFamily: "'Georgia', serif" }}>
        <div style={{ maxWidth: 560, margin: '0 auto', padding: isMobile ? '20px 16px 40px' : '40px 24px' }}>
          <button onClick={handleLeave} style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}>
            ← Back to Lobby
          </button>

          {error && <div style={{ background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>}

          <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, padding: isMobile ? 20 : 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <h2 style={{ fontSize: isMobile ? 18 : 22, margin: 0 }}>Waiting Room</h2>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: gameMode === 'euchre' ? 'rgba(14,165,233,0.15)' : 'rgba(34,197,94,0.15)', color: gameMode === 'euchre' ? '#0ea5e9' : '#22c55e', border: `1px solid ${gameMode === 'euchre' ? '#0ea5e940' : '#22c55e40'}` }}>
                {gameMode === 'euchre' ? 'Partner Euchre' : 'Buck Euchre'}
              </span>
              {isPrivate && <span style={{ fontSize: 11, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 4, padding: '2px 8px' }}>Private</span>}
            </div>

            {/* Room code */}
            <div style={{ background: '#0d1117', border: '2px solid #58a6ff', borderRadius: 10, padding: '14px 20px', textAlign: 'center', marginBottom: 20, cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(roomCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, letterSpacing: 10, fontFamily: 'monospace', color: '#58a6ff' }}>{roomCode}</div>
              <div style={{ fontSize: 11, color: '#8b949e', marginTop: 4 }}>{copied ? '✓ Copied!' : 'Tap to copy code'}</div>
            </div>

            {/* Player seats */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 8 }}>
                Players ({players.length}/{humanSlots}{aiCount > 0 ? ` + ${aiCount} AI` : ''})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : `repeat(${Math.min(playerCount, 4)}, 1fr)`, gap: 8 }}>
                {Array.from({ length: playerCount }).map((_, i) => {
                  const player = players.find(p => p.seatIndex === i);
                  const isAiSeat = i >= humanSlots;
                  return (
                    <div key={i} style={{ background: '#0d1117', border: `1px solid ${player || isAiSeat ? '#238636' : '#30363d'}`, borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: player ? '#238636' : isAiSeat ? '#1f6feb' : '#21262d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 auto 6px' }}>
                        {i + 1}
                      </div>
                      <div style={{ fontSize: 12, color: player || isAiSeat ? '#e2e8f0' : '#4b5563', fontWeight: player ? 600 : 400 }}>
                        {isAiSeat ? 'AI' : player ? player.nickname : 'Open'}
                      </div>
                      {player && i === 0 && isHost && <div style={{ fontSize: 9, color: '#f59e0b', marginTop: 2 }}>HOST</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Start / Leave */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {isHost && (
                <button onClick={handleStartGame} disabled={!allSeated} style={{ flex: 1, background: allSeated ? '#238636' : '#21262d', color: allSeated ? '#fff' : '#4b5563', border: 'none', borderRadius: 8, padding: '12px', fontSize: 15, fontWeight: 700, cursor: allSeated ? 'pointer' : 'not-allowed' }}>
                  {allSeated ? 'Start Game' : `Waiting for ${humanSlots - players.length} more...`}
                </button>
              )}
              {!isHost && <div style={{ flex: 1, background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '12px', fontSize: 13, color: '#8b949e', textAlign: 'center' }}>Waiting for host to start...</div>}
              <button onClick={handleLeave} style={{ background: 'transparent', border: '1px solid #30363d', color: '#8b949e', borderRadius: 8, padding: '12px 16px', fontSize: 13, cursor: 'pointer' }}>Leave</button>
            </div>

            {/* Room Chat */}
            <div style={{ borderTop: '1px solid #21262d', paddingTop: 16 }}>
              <div style={{ color: '#8b949e', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Room Chat</div>
              <div ref={lobbyMsgsRef} style={{ height: isMobile ? 130 : 160, overflowY: 'auto', background: '#0d1117', borderRadius: 8, border: '1px solid #21262d', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                {lobbyMessages.length === 0
                  ? <div style={{ color: '#484f58', fontSize: 12, fontStyle: 'italic', margin: 'auto', textAlign: 'center' }}>Say hello while you wait!</div>
                  : lobbyMessages.map((msg, i) => (
                    <div key={i} style={{ fontSize: 13 }}>
                      <span style={{ color: '#58a6ff', fontWeight: 600 }}>{msg.nickname}: </span>
                      <span style={{ color: '#c9d1d9' }}>{msg.text}</span>
                    </div>
                  ))
                }
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={lobbyInput} onChange={e => setLobbyInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendLobbyMessage()} placeholder="Say something..." maxLength={200}
                  style={{ flex: 1, background: '#0d1117', border: '1px solid #30363d', borderRadius: 6, color: '#e2e8f0', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                <button onClick={sendLobbyMessage} style={{ background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Send</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── LOBBY VIEW ────────────────────────────────────────────────────────────
  return (
    <div style={{ ...hardwoodFloorStyle, minHeight: '100vh', color: '#e2e8f0', fontFamily: "'Georgia', serif" }}>
      <div style={{ maxWidth: 700, margin: '0 auto', padding: isMobile ? '20px 16px 40px' : '36px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 20 : 28 }}>
          <div>
            <button onClick={() => navigate('/home')} style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6, display: 'block' }}>← Home</button>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, margin: 0, color: '#e2e8f0' }}>Community Room</h1>
          </div>
          <span style={{ fontSize: 11, color: socketStatus === 'connected' ? '#3fb950' : socketStatus === 'connecting' ? '#d29922' : '#f85149' }}>
            {socketStatus === 'connected' ? '● Online' : socketStatus === 'connecting' ? '● Connecting...' : '● Offline'}
          </span>
        </div>

        {error && <div style={{ background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: '#f85149', cursor: 'pointer', fontSize: 14 }}>×</button></div>}

        {/* ── CREATE GAME BUTTONS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 24 : 32 }}>
          {/* Create Euchre */}
          <button onClick={() => setShowCreateModal('euchre')} style={{
            background: 'linear-gradient(135deg, #0c1a2e, #0a2540)', border: '2px solid #0ea5e960',
            borderRadius: isMobile ? 12 : 16, padding: isMobile ? '20px 14px' : '28px 20px',
            cursor: 'pointer', textAlign: 'left', color: '#e2e8f0', position: 'relative', overflow: 'hidden',
            transition: 'all 0.2s', minHeight: isMobile ? 110 : 140
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(14,165,233,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
            <span style={{ position: 'absolute', top: isMobile ? 10 : 14, right: isMobile ? 12 : 16, fontSize: isMobile ? 28 : 36, opacity: 0.2, color: '#0ea5e9' }}>♣</span>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#0ea5e9', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Create Game</div>
            <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, color: '#0ea5e9', lineHeight: 1.1 }}>Euchre</div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#8b949e', marginTop: 4 }}>Partner / Team</div>
          </button>

          {/* Create Buck Euchre */}
          <button onClick={() => setShowCreateModal('buck')} style={{
            background: 'linear-gradient(135deg, #0c1f0c, #0a2a12)', border: '2px solid #22c55e60',
            borderRadius: isMobile ? 12 : 16, padding: isMobile ? '20px 14px' : '28px 20px',
            cursor: 'pointer', textAlign: 'left', color: '#e2e8f0', position: 'relative', overflow: 'hidden',
            transition: 'all 0.2s', minHeight: isMobile ? 110 : 140
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(34,197,94,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
            <span style={{ position: 'absolute', top: isMobile ? 10 : 14, right: isMobile ? 12 : 16, fontSize: isMobile ? 28 : 36, opacity: 0.2, color: '#22c55e' }}>♠</span>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#22c55e', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Create Game</div>
            <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, color: '#22c55e', lineHeight: 1.1 }}>Buck Euchre</div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#8b949e', marginTop: 4 }}>Wager bucks</div>
          </button>
        </div>

        {/* ── JOIN BY CODE ── */}
        <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #30363d', borderRadius: 10, padding: isMobile ? '14px 16px' : '16px 20px', marginBottom: isMobile ? 24 : 32, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: '#8b949e', fontSize: 13, flexShrink: 0 }}>Have a code?</span>
          <input value={joinCodeInput} onChange={e => setJoinCodeInput(e.target.value.toUpperCase().slice(0, 6))} placeholder="ABC123" maxLength={6}
            style={{ width: 110, padding: '7px 12px', background: '#0d1117', border: '1px solid #30363d', borderRadius: 6, color: '#e2e8f0', fontSize: 16, fontWeight: 700, textAlign: 'center', letterSpacing: 4, fontFamily: 'monospace', outline: 'none' }} />
          <button onClick={() => handleJoinRoom()} disabled={joinCodeInput.length < 6} style={{ background: joinCodeInput.length === 6 ? '#7c3aed' : '#21262d', color: joinCodeInput.length === 6 ? '#fff' : '#4b5563', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: joinCodeInput.length === 6 ? 'pointer' : 'not-allowed' }}>
            Join
          </button>
        </div>

        {/* ── OPEN ROOMS ── */}
        {waitingRooms.length > 0 && (
          <div style={{ marginBottom: isMobile ? 24 : 32 }}>
            <h2 style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: '#c9d1d9', marginBottom: 12 }}>
              Open Rooms — Waiting for Players
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {waitingRooms.map(room => {
                const hs = room.playerCount - room.aiCount;
                const spotsLeft = hs - room.currentPlayers;
                const playerList = room.playerNicknames || [];
                return (
                  <div key={room.roomCode} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 10, padding: isMobile ? '14px 16px' : '16px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{room.hostNickname}'s Room</span>
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: room.gameMode === 'euchre' ? 'rgba(14,165,233,0.15)' : 'rgba(34,197,94,0.15)', color: room.gameMode === 'euchre' ? '#0ea5e9' : '#22c55e', border: `1px solid ${room.gameMode === 'euchre' ? '#0ea5e940' : '#22c55e40'}` }}>
                          {room.gameMode === 'euchre' ? 'Euchre' : 'Buck Euchre'}
                        </span>
                      </div>
                      {/* Player name bubbles */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {Array.from({ length: room.playerCount }).map((_, i) => {
                          const isAi = i >= hs;
                          const nick = isAi ? 'AI' : (playerList[i] || null);
                          return (
                            <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: nick ? '#238636' : isAi ? '#1f6feb' : '#21262d', color: nick || isAi ? '#fff' : '#4b5563', border: `1px solid ${nick ? '#238636' : isAi ? '#1f6feb' : '#30363d'}` }}>
                              {nick || 'Open'}
                            </span>
                          );
                        })}
                        {spotsLeft > 0 && <span style={{ fontSize: 11, color: '#8b949e' }}>{spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} open</span>}
                      </div>
                    </div>
                    <button onClick={() => handleJoinRoom(room.roomCode)} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                      Join
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── LIVE GAMES ── */}
        {liveRooms.length > 0 && (
          <div style={{ marginBottom: isMobile ? 24 : 32 }}>
            <h2 style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: '#c9d1d9', marginBottom: 12 }}>
              Live Games
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {liveRooms.map(room => {
                const playerList = room.playerNicknames || [];
                return (
                  <div key={room.roomCode} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 10, padding: isMobile ? '14px 16px' : '16px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{room.hostNickname}'s Game</span>
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: room.gameMode === 'euchre' ? 'rgba(14,165,233,0.15)' : 'rgba(34,197,94,0.15)', color: room.gameMode === 'euchre' ? '#0ea5e9' : '#22c55e', border: `1px solid ${room.gameMode === 'euchre' ? '#0ea5e940' : '#22c55e40'}` }}>
                          {room.gameMode === 'euchre' ? 'Euchre' : 'Buck Euchre'}
                        </span>
                        <span style={{ fontSize: 10, color: '#f85149' }}>● LIVE</span>
                        {room.handNumber && <span style={{ fontSize: 10, color: '#8b949e' }}>Hand {room.handNumber}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {playerList.map((nick, i) => (
                          <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d' }}>{nick}</span>
                        ))}
                        {room.spectatorCount > 0 && <span style={{ fontSize: 11, color: '#8b949e' }}>· {room.spectatorCount} watching</span>}
                      </div>
                    </div>
                    {!room.isPhantom && (
                      <button onClick={async () => {
                        const result = await spectateRoom(room.roomCode);
                        if (result.success) {
                          navigate(`/play?community=${room.roomCode}&spectate=true&players=${room.playerCount}`);
                        } else {
                          setError(result.error || 'Cannot spectate');
                        }
                      }} style={{ background: '#1e293b', color: '#c4b5fd', border: '1px solid #7c3aed', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                        Watch
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {waitingRooms.length === 0 && liveRooms.length === 0 && (
          <div style={{ textAlign: 'center', padding: isMobile ? '32px 0' : '48px 0', color: '#484f58' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>♠ ♥ ♦ ♣</div>
            <div style={{ fontSize: isMobile ? 15 : 17, marginBottom: 6 }}>No active games right now</div>
            <div style={{ fontSize: 13 }}>Create a game above to get started!</div>
          </div>
        )}
      </div>

      {/* ── CREATE GAME MODAL ── */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowCreateModal(null)}>
          <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 14, padding: isMobile ? 20 : 28, width: '100%', maxWidth: 400 }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: isMobile ? 18 : 22, marginBottom: 20, color: showCreateModal === 'euchre' ? '#0ea5e9' : '#22c55e' }}>
              {showCreateModal === 'euchre' ? '♣ Create Euchre Game' : '♠ Create Buck Euchre Game'}
            </h2>

            {showCreateModal === 'buck' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 8 }}>Players</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[3, 4].map(n => (
                    <button key={n} onClick={() => setPlayerCount(n)} style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', background: playerCount === n ? '#238636' : '#0d1117', color: playerCount === n ? '#fff' : '#8b949e', border: playerCount === n ? 'none' : '1px solid #30363d' }}>
                      {n} Players
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showCreateModal === 'euchre' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 8 }}>AI Fill-ins</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[0, 1, 2].map(n => (
                    <button key={n} onClick={() => setAiCount(n)} style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: aiCount === n ? '#1f6feb' : '#0d1117', color: aiCount === n ? '#fff' : '#8b949e', border: aiCount === n ? 'none' : '1px solid #30363d' }}>
                      {n === 0 ? 'None' : `${n} AI`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showCreateModal === 'buck' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 8 }}>AI Fill-ins</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[0, 1].map(n => (
                    <button key={n} onClick={() => setAiCount(n)} style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: aiCount === n ? '#1f6feb' : '#0d1117', color: aiCount === n ? '#fff' : '#8b949e', border: aiCount === n ? 'none' : '1px solid #30363d' }}>
                      {n === 0 ? 'None' : '1 AI'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, padding: '10px 14px', background: '#0d1117', borderRadius: 8, border: `1px solid ${isPrivate ? '#f59e0b40' : '#30363d'}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: isPrivate ? '#f59e0b' : '#e2e8f0' }}>Private Room</div>
                <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2 }}>{isPrivate ? 'Code only — not listed' : 'Visible in lobby'}</div>
              </div>
              <div onClick={() => setIsPrivate(v => !v)} style={{ width: 36, height: 18, borderRadius: 9, background: isPrivate ? '#f59e0b' : '#4b5563', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: isPrivate ? 21 : 3, transition: 'left 0.2s' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                disabled={socketStatus !== 'connected'}
                onClick={() => handleCreateRoom(
                  showCreateModal,
                  showCreateModal === 'euchre' ? 4 : playerCount,
                  aiCount,
                  showCreateModal === 'euchre' ? partnerMode : 'fixed',
                  isPrivate
                )}
                style={{ flex: 1, background: socketStatus === 'connected' ? (showCreateModal === 'euchre' ? '#0ea5e9' : '#238636') : '#21262d', color: socketStatus === 'connected' ? '#fff' : '#4b5563', border: 'none', borderRadius: 8, padding: '13px', fontSize: 15, fontWeight: 700, cursor: socketStatus === 'connected' ? 'pointer' : 'not-allowed' }}>
                {socketStatus === 'connected' ? 'Create Room' : socketStatus === 'connecting' ? 'Connecting...' : '● Not Connected'}
              </button>
              <button onClick={() => setShowCreateModal(null)} style={{ background: 'transparent', border: '1px solid #30363d', color: '#8b949e', borderRadius: 8, padding: '13px 16px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
